// Single source of truth for the home-page timeline, the nav menu and
// the prev/next links on detail pages. Ordered newest first.
//
// Fields:
//   ongoing   -> renders a pulsing "live" node on the timeline
//   metrics   -> highest-signal element for a recruiter; keep to <= 3
//   sameOrgAsPrevious -> tighter node styling for a repeat employer (role progression)

import watoLogo    from '../icons/wato_icon.jpeg';
import envgoLogo   from '../icons/envgo_logo2.jpeg';
import goodlabsLogo from '../icons/goodlabs.png';
import frcLogo     from '../icons/FRC-Vertical.png';
import rocketryLogo from '../icons/rocketry.png';
import deeptrekkerLogo from '../icons/deeptrekker_icon.png';

import watoCover from '../assets/covers/watoCover.jpg';
import watoCover2 from '../assets/covers/watoCover2.jpg';
import envgoCover from '../assets/covers/envgoCover.jpg';
import goodlabsCover from '../assets/covers/goodlabsCover.jpg';
import goodlabsRlCover from '../assets/covers/goodlabsRlCover.jpg';
import frcCover from '../assets/covers/frcCover.jpg';
import roots2stemCover from '../assets/covers/roots2stemCover.jpg';
import deeptrekkerCover from '../assets/covers/deeptrekkerCover.jpg';

const experiences = [
  {
    id: 'watonomous-director',
    org: 'WATonomous',
    role: 'Director',
    dateLabel: 'May 2026 - Present',
    sortKey: '2026-05',
    ongoing: true,
    kind: 'leadership',
    location: 'Waterloo, ON',
    logo: watoLogo,
    cover: watoCover,
    route: '/watonomous',
    summary:
      "Directing the University of Waterloo's autonomous vehicle team across perception, prediction, control and simulation.",
    stack: ['ROS 2', 'BEVFusion', 'Transformers', 'Pure Pursuit', 'CARLA', 'SIL', 'LiDAR', 'Docker'],
    highlights: [
      'Directing the University of Waterloo autonomous vehicle design team, owning technical direction across the full stack and overseeing on-vehicle testing.',
      'Driving end-to-end perception development on BEVFusion, fusing camera and LiDAR into a unified bird’s-eye-view representation.',
      'Overseeing a new transformer-based prediction model for agent trajectory forecasting.',
      'Overseeing new pure-pursuit controllers for path tracking.',
      'Standing up software-in-the-loop testing through CARLA so the stack is validated in simulation before it reaches the vehicle.',
    ],
  },

  {
    id: 'deeptrekker',
    org: 'DeepTrekker',
    role: 'Computer Vision & Robotics Software Co-op',
    dateLabel: 'Jan 2026 - Aug 2026',
    sortKey: '2026-01',
    kind: 'internship',
    location: 'Ontario, CA',
    logo: deeptrekkerLogo,
    cover: deeptrekkerCover,
    route: '/deeptrekker',
    summary:
      'Built the production zero-copy stereo vision and sonar stack for the SPECTRA ROV on Jetson Orin AGX 64 GB.',
    stack: [
      'C++', 'CUDA', 'ROS 2', 'Isaac ROS / NITROS', 'libargus', 'VPI',
      'GStreamer', 'WebRTC', 'OpenCV CUDA', 'Protobuf', 'SLAM', 'Jetson Orin AGX',
    ],
    metrics: [
      { value: '4K60', label: 'onboard record, zero-copy' },
      { value: '1920×1920', label: 'stereo WebRTC encode @60' },
      { value: '1 ms', label: 'turbidity correction' },
    ],
    highlights: [
      'Built an end-to-end zero-copy stereo camera pipeline on Jetson Orin AGX: a libargus-backed Isaac ROS NITROS publishing node in C++ with full control over contrast, saturation, exposure and AWB, plus a custom CUDA kernel for digital brightness, emitting contiguous NV12. Sustains 4K60 single-camera onboard recording alongside dual 1920×1920 60 FPS streaming encodes and a 1920×1920 30 FPS turbidity stream.',
      'Wrote CUDA-stream NV12 resize (VPI warp) and rectify nodes to work around Isaac ROS image builder’s lack of padded-NV12 publishing, then an encoder node ingesting NV12 directly into GStreamer as wrapped NvBufSurfaces into nvv4l2h264enc and on into WebRTC, keeping the whole camera-to-wire path zero-copy and low-latency.',
      'Developed a real-time turbidity node running with OpenCV CUDA across CUDA streams at 1 ms per frame, producing clear imagery in murky water.',
      'Authored a C++ zero-copy driver for the Water Linked 3D-15 sonar over a Protobuf framework, and contributed to driver development for Oculus and Water Linked sonar systems.',
      'Improved SLAM via parameter tuning for local loop closure, and developed a novel global appearance-based point-cloud loop-closure pipeline using MapClosure for prior detection with KISS-Matcher / ICP registration.',
      'Delivered a production stereo-calibration procedure and operator UI for the manufacturing line, validated against photogrammetry to guarantee customer-facing accuracy specs.',
    ],
  },

  {
    id: 'envgo',
    org: 'ENVGO',
    role: 'Autonomy Software Development Co-op',
    dateLabel: 'May 2025 - Dec 2025',
    sortKey: '2025-05',
    kind: 'internship',
    location: 'Toronto, ON',
    logo: envgoLogo,
    cover: envgoCover,
    route: '/envgo',
    summary:
      "Established the company's first perception system from scratch for a hydrofoiling electric boat.",
    stack: ['ROS 2', 'C++', 'TensorRT', 'CUDA', 'Jetson Orin', 'WebRTC', 'GStreamer', 'Semantic Segmentation'],
    metrics: [
      { value: '100 FPS', label: 'segmentation on Jetson Orin' },
    ],
    highlights: [
      'Designed and implemented a multi-sensor autonomy pipeline integrating cameras and radar for a hydrofoiling electric boat, establishing the company’s first perception system from scratch.',
      'Developed a ROS 2 C++ perception node with a semantic segmentation model for free-space detection, training on both open-source datasets and custom-labeled data, and optimized inference to 100 FPS on Jetson Orin using TensorRT batching and CUDA kernels.',
      'Built customer-facing situational awareness features (surround view, front/back view) with real-time video streaming through WebRTC and Jetson-optimized GStreamer pipelines.',
    ],
  },

  {
    id: 'watonomous-perception',
    org: 'WATonomous',
    role: 'Perception & Team Lead',
    dateLabel: 'Feb 2024 - Apr 2026',
    sortKey: '2024-02',
    kind: 'leadership',
    location: 'Waterloo, ON',
    logo: watoLogo,
    cover: watoCover2,
    route: '/watonomous',
    sameOrgAsPrevious: 'watonomous-director',
    summary:
      'Led a 20-member perception team and shipped the segmentation node on the autonomous vehicle.',
    stack: ['ROS 2', 'Python', 'Docker', 'LiDAR', 'TensorRT', 'Segmentation'],
    metrics: [{ value: '20', label: 'engineers mentored' }],
    highlights: [
      'Led a team of 20 members with weekly meetings and work sessions, mentoring members on a Dockerized ROS software stack for tasks like depth estimation and 2D-to-3D object tracking.',
      'Programmed and deployed a ROS 2 Python node for the autonomous car, integrating the segmentation model with the vehicle’s perception pipeline for a more accurate and robust understanding of the driving environment.',
      'Integrated camera and LiDAR drivers and troubleshot network communication between sensors, using Docker and ROS diagnostics.',
    ],
  },

  {
    id: 'goodlabs-robot',
    org: 'GoodLabs Studio',
    role: 'Bipedal Robotics Engineering Co-op',
    dateLabel: '2023 - 2024',
    sortKey: '2023-05',
    kind: 'internship',
    location: 'Toronto, ON',
    logo: goodlabsLogo,
    cover: goodlabsCover,
    route: '/goodlabsrobotics',
    summary: 'Designed, built and controlled a 17-DOF 3D-printed bipedal robot.',
    stack: ['C++', 'Python', 'PID Control', 'Jetson Nano', 'Docker', 'CAD', '3D Printing'],
    metrics: [{ value: '17 DOF', label: 'bipedal platform' }],
    highlights: [
      'Designed a 17 DOF bipedal robot with 3D printed parts to mimic human-like walking.',
      'Assembled electronics including designing PDPs, controllers and communication systems.',
      'Developed an interface for robot control through Jetson Nano using Jupyter Notebook and Docker.',
      'Programmed in Python and C++ to develop walking algorithms and PID control systems.',
    ],
  },

  {
    id: 'goodlabs-rl',
    org: 'GoodLabs Studio',
    role: 'Reinforcement Learning Engineering Co-op',
    dateLabel: '2023 - 2024',
    sortKey: '2023-04',
    kind: 'research',
    location: 'Toronto, ON',
    logo: goodlabsLogo,
    cover: goodlabsRlCover,
    route: '/goodlabsrl',
    sameOrgAsPrevious: 'goodlabs-robot',
    summary: 'Trained a PPO policy in Isaac Gym that produced natural, untethered walking.',
    stack: ['PyTorch', 'PPO', 'Isaac Gym', 'Sim-to-Real', 'Python'],
    highlights: [
      'Developed a proximal policy optimization reinforcement learning algorithm in PyTorch to produce natural walking for the robot.',
      'Synthesized human gait patterns into reward functions, including a custom reward following an 8-stage human gait cycle, to produce optimized and natural walking.',
      'Produced a near-accurate model of the robot in Isaac Gym’s simulation environment to ensure easy sim-to-real transfer.',
    ],
  },

  {
    id: 'rocketry',
    org: 'Waterloo Rocketry',
    role: 'Software Contributor',
    dateLabel: '2023 - 2024',
    sortKey: '2023-01',
    kind: 'team',
    location: 'Waterloo, ON',
    logo: rocketryLogo,
    cover: null,
    route: 'https://github.com/waterloo-rocketry/website-react/commits?author=lucasreljic',
    external: true,
    summary: 'Contributed to the team website and tooling for the Waterloo Rocketry design team.',
    stack: ['React', 'JavaScript'],
    highlights: [
      'Contributed features and fixes to the Waterloo Rocketry React website.',
    ],
  },

  {
    id: 'frc4627',
    org: 'FRC Team 4627',
    role: 'Team Captain',
    dateLabel: '2020 - 2022',
    sortKey: '2020-09',
    kind: 'leadership',
    location: 'Ontario, CA',
    logo: frcLogo,
    cover: frcCover,
    route: '/frc4627',
    summary: 'Captained a 30-student competition robotics team; owned Java robot code and the vision system.',
    stack: ['Java', 'MicroPython', 'OpenMV', 'PID Control', 'Computer Vision'],
    metrics: [{ value: '30+', label: 'students organized' }],
    highlights: [
      'Served as team captain in the 2022 season, organizing daily meetings and supervising the design and production of the robot across 30+ students.',
      'Programmed in Java for the robot and MicroPython for the OpenMV vision system, including a JSON pipeline between the Java and Python code.',
      'Developed PID control loops for all systems on the robot.',
      'Taught new members Java and Python.',
    ],
  },

  {
    id: 'roots2stem',
    org: 'Roots 2 STEM',
    role: 'Junior Engineering Club',
    dateLabel: '2014 - 2018',
    sortKey: '2014-01',
    kind: 'team',
    location: 'Ontario, CA',
    logo: null,
    cover: roots2stemCover,
    route: '/roots2stem',
    summary: 'Where it started: rovers, RC airplanes and an electric go-kart, from age 10.',
    stack: ['Fabrication', 'Welding', '3D Printing', 'Electronics'],
    highlights: [
      'Designed and built rovers, RC airplanes and an electric go-kart with access to 3D printers and arc welders.',
    ],
  },
];

export default experiences;
